import React from 'react';
import '@/index.css';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';

// Pages
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import PricingPage from '@/pages/PricingPage';
import DashboardPage from '@/pages/DashboardPage';
import CasesPage from '@/pages/CasesPage';
import CaseFormPage from '@/pages/CaseFormPage';
import CaseDetailPage from '@/pages/CaseDetailPage';
import BulkImportPage from '@/pages/BulkImportPage';
import ReportsPage from '@/pages/ReportsPage';
import FormFillPage from '@/pages/FormFillPage';
import AdminUsersPage from '@/pages/AdminUsersPage';
import AdminUserFormPage from '@/pages/AdminUserFormPage';
import OrganizationsPage from '@/pages/OrganizationsPage';
import OrganizationFormPage from '@/pages/OrganizationFormPage';
import ClientPortalLoginPage from '@/pages/ClientPortalLoginPage';
import ClientPortalDashboard from '@/pages/ClientPortalDashboard';
import NotificationsPage from '@/pages/NotificationsPage';
import NotificationCenterPage from '@/pages/NotificationCenterPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import SchedulerPage from '@/pages/SchedulerPage';
import FAQPage from '@/pages/FAQPage';
import InvoicesPage from '@/pages/InvoicesPage';
import CalendarPage from '@/pages/CalendarPage';
import BatchScanPage from '@/pages/BatchScanPage';
import EmailTemplatesPage from '@/pages/EmailTemplatesPage';
import DataCollectionPage from '@/pages/DataCollectionPage';
import SecuritySettingsPage from '@/pages/SecuritySettingsPage';
import AIAssistantPage from '@/pages/AIAssistantPage';
import OnlineFilingPage from '@/pages/OnlineFilingPage';
import BillingPage from '@/pages/BillingPage';
import FloatingChat from '@/components/FloatingChat';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    const redirect = searchParams.get('redirect');
    return <Navigate to={redirect || '/dashboard'} replace />;
  }
  
  return children;
};

// Redirect to external/static pages
const RedirectToExternal = ({ url }) => {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/faq" element={<FAQPage />} />
      
      {/* Marketing Redirect Routes */}
      <Route path="/demo" element={<RedirectToExternal url="/api/docs/CONTACT.html?type=demo" />} />
      <Route path="/trial" element={<RedirectToExternal url="/api/docs/CONTACT.html?type=trial" />} />
      <Route path="/free-trial" element={<RedirectToExternal url="/api/docs/CONTACT.html?type=trial" />} />
      <Route path="/contact" element={<RedirectToExternal url="/api/docs/CONTACT.html" />} />
      <Route path="/overview" element={<RedirectToExternal url="/api/docs/ADVERTISEMENT.html" />} />
      
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ForgotPasswordPage />} />
      
      {/* Client Portal Routes (Public) */}
      <Route path="/client-portal" element={<ClientPortalLoginPage />} />
      <Route path="/client-portal/dashboard" element={<ClientPortalDashboard />} />
      
      {/* Data Collection (Public) */}
      <Route path="/collect/:token" element={<DataCollectionPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/cases" element={
        <ProtectedRoute>
          <CasesPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/new" element={
        <ProtectedRoute>
          <CaseFormPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/:id" element={
        <ProtectedRoute>
          <CaseDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/import" element={
        <ProtectedRoute>
          <BulkImportPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/batch-scan" element={
        <ProtectedRoute>
          <BatchScanPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/forms" element={
        <ProtectedRoute>
          <FormFillPage />
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users/new" element={
        <ProtectedRoute>
          <AdminUserFormPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users/:id/edit" element={
        <ProtectedRoute>
          <AdminUserFormPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/organizations" element={
        <ProtectedRoute>
          <OrganizationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/organizations/new" element={
        <ProtectedRoute>
          <OrganizationFormPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/organizations/:id/edit" element={
        <ProtectedRoute>
          <OrganizationFormPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationCenterPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute>
          <AuditLogsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-templates" element={
        <ProtectedRoute>
          <EmailTemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/scheduler" element={
        <ProtectedRoute>
          <SchedulerPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/security" element={
        <ProtectedRoute>
          <SecuritySettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/ai-assistant" element={
        <ProtectedRoute>
          <AIAssistantPage />
        </ProtectedRoute>
      } />
      <Route path="/efiling" element={
        <ProtectedRoute>
          <OnlineFilingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/billing" element={
        <ProtectedRoute requiredRole="super_admin">
          <BillingPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy redirects */}
      <Route path="/clients" element={<Navigate to="/cases" replace />} />
      <Route path="/clients/*" element={<Navigate to="/cases" replace />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <FloatingChat />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
